"use client";

import { AlertCircle, Loader2, RefreshCw } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { getDeviceInfo } from "@/core/device";
import { useTranslation } from "@/core/i18n";
import type { Dictionary } from "@/core/i18n/__generated__/dictionary";
import type { CameraErrorKind } from "@/features/camera/useCameraSession";
const PERMISSION_PROMPT_ATTEMPTS_SESSION_KEY = "ageless-camera-permission-prompt-attempts";
const PERMISSION_PROMPT_ESCALATION_THRESHOLD = 2;
type CaptureErrorOverlayProps = {
  errorKind: CameraErrorKind | null;
  // onRetry may be synchronous or async; when async the overlay awaits it so
  // the button can stay in a visible "Trying…" state until the attempt
  // resolves. This is what tells users their click was received even when the
  // underlying getUserMedia rejects instantly.
  onRetry: () => Promise<unknown> | void;
  onReload?: () => void;
  // Escape hatch to the photo-upload flow. When provided, the overlay offers an
  // "Upload a photo instead" action for failure kinds where the camera is
  // unlikely to recover in-session (blocked permission, embed policy, no
  // device). The parent MUST handle this by transitioning the capture
  // state machine to the upload stage so `useCameraSession` unmounts —
  // otherwise its focus/visibilitychange auto-retry re-triggers getUserMedia
  // when the user returns from the OS file picker and fights the upload.
  onUploadInstead?: () => void;
};
type OverlayCopy = {
  title: keyof Dictionary;
  body: keyof Dictionary;
  instructions?: ReadonlyArray<keyof Dictionary>;
  primaryAction: "retry" | "reload";
  // Secondary escape-hatch action. Used on `permission-denied` because iOS
  // Safari and some in-app browsers (Instagram, TikTok) frequently require a
  // full page reload after the user toggles permission in settings — the
  // Permissions API `change` event and focus-based retry can both miss it.
  secondaryAction?: "reload";
};
function getInstructionKeysForPlatform(platform: ReturnType<typeof getDeviceInfo>["platform"]): ReadonlyArray<keyof Dictionary> {
  if (platform === "ios") {
    return ["assessmentSteps.studioSession.error.camera.permissionDenied.ios.step1", "assessmentSteps.studioSession.error.camera.permissionDenied.ios.step2"];
  }
  if (platform === "android") {
    return ["assessmentSteps.studioSession.error.camera.permissionDenied.android.step1", "assessmentSteps.studioSession.error.camera.permissionDenied.android.step2"];
  }
  return ["assessmentSteps.studioSession.error.camera.permissionDenied.desktop.step1", "assessmentSteps.studioSession.error.camera.permissionDenied.desktop.step2", "assessmentSteps.studioSession.error.camera.permissionDenied.desktop.step3"];
}
function getOverlayCopy(errorKind: CameraErrorKind, platform: ReturnType<typeof getDeviceInfo>["platform"]): OverlayCopy {
  switch (errorKind) {
    case "permission-denied":
      return {
        title: "assessmentSteps.studioSession.error.camera.permissionDenied.title",
        body: "assessmentSteps.studioSession.error.camera.permissionDenied.body",
        instructions: getInstructionKeysForPlatform(platform),
        primaryAction: "retry",
        secondaryAction: "reload"
      };
    case "permission-prompt":
      // User dismissed the permission prompt without choosing. No settings
      // instructions — they just need to tap Try Again and answer the prompt.
      return {
        title: "assessmentSteps.studioSession.error.camera.permissionPrompt.title",
        body: "assessmentSteps.studioSession.error.camera.permissionPrompt.body",
        primaryAction: "retry"
      };
    case "no-camera":
      return {
        title: "assessmentSteps.studioSession.error.camera.noCamera.title",
        body: "assessmentSteps.studioSession.error.camera.noCamera.body",
        primaryAction: "retry"
      };
    case "in-use":
      return {
        title: "assessmentSteps.studioSession.error.camera.inUse.title",
        body: "assessmentSteps.studioSession.error.camera.inUse.body",
        primaryAction: "retry"
      };
    case "no-media-devices":
      return {
        title: "assessmentSteps.studioSession.error.camera.noMediaDevices.title",
        body: "assessmentSteps.studioSession.error.camera.noMediaDevices.body",
        primaryAction: "reload"
      };
    case "embed-policy-blocked":
      // The host page's iframe is missing `allow="camera"` (or a parent
      // `Permissions-Policy` header strips camera). Browser-settings steps
      // are useless here — only the website owner can fix it. Use "reload"
      // for consistency with `no-media-devices` so the visible action stays
      // familiar; the body copy is what tells the user (or the support
      // engineer reading over their shoulder) where the problem actually
      // lives.
      return {
        title: "assessmentSteps.studioSession.error.camera.embedPolicyBlocked.title",
        body: "assessmentSteps.studioSession.error.camera.embedPolicyBlocked.body",
        primaryAction: "reload"
      };
    case "generic":
      return {
        title: "assessmentSteps.studioSession.error.camera.generic.title",
        body: "assessmentSteps.studioSession.error.camera.generic.body",
        primaryAction: "retry"
      };
  }
}
function reloadPage() {
  if (typeof window !== "undefined") {
    window.location.reload();
  }
}
function readPermissionPromptAttempts(): number {
  if (typeof window === "undefined") {
    return 0;
  }
  const raw = window.sessionStorage.getItem(PERMISSION_PROMPT_ATTEMPTS_SESSION_KEY);
  if (!raw) {
    return 0;
  }
  const parsed = Number.parseInt(raw, 10);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return 0;
  }
  return parsed;
}
function persistPermissionPromptAttempts(value: number): void {
  if (typeof window === "undefined") {
    return;
  }
  window.sessionStorage.setItem(PERMISSION_PROMPT_ATTEMPTS_SESSION_KEY, value.toString());
}
function resetPermissionPromptAttempts(): void {
  if (typeof window === "undefined") {
    return;
  }
  window.sessionStorage.removeItem(PERMISSION_PROMPT_ATTEMPTS_SESSION_KEY);
}
export function CaptureErrorOverlay({
  errorKind,
  onRetry,
  onReload = reloadPage,
  onUploadInstead
}: CaptureErrorOverlayProps): React.ReactElement | null {
  const {
    t
  } = useTranslation();
  const {
    platform,
    isInAppBrowser
  } = useMemo(() => getDeviceInfo(), []);
  const [isRetrying, setIsRetrying] = useState(false);
  const [permissionPromptAttempts, setPermissionPromptAttempts] = useState(readPermissionPromptAttempts);
  const previousErrorKindRef = useRef<CameraErrorKind | null>(null);
  // The errorKind captured at the moment the user last clicked Try Again.
  // If after the retry completes the prop is unchanged and still non-null,
  // we know the retry produced no progress and surface a hint so the user
  // doesn't assume the button is broken.
  const [retriedKind, setRetriedKind] = useState<CameraErrorKind | null>(null);
  useEffect(() => {
    if (!errorKind) {
      setPermissionPromptAttempts(0);
      resetPermissionPromptAttempts();
      previousErrorKindRef.current = errorKind;
      return;
    }
    if (errorKind === "permission-prompt" && previousErrorKindRef.current !== "permission-prompt") {
      setPermissionPromptAttempts(current => {
        const next = current + 1;
        persistPermissionPromptAttempts(next);
        return next;
      });
    }
    previousErrorKindRef.current = errorKind;
  }, [errorKind]);
  if (!errorKind) return null;
  const shouldEscalatePermissionPrompt = errorKind === "permission-prompt" && permissionPromptAttempts >= PERMISSION_PROMPT_ESCALATION_THRESHOLD;
  const displayErrorKind = shouldEscalatePermissionPrompt ? "permission-denied" : errorKind;
  const copy = getOverlayCopy(displayErrorKind, platform);
  const handleRetryClick = async () => {
    if (errorKind === "permission-prompt") {
      setPermissionPromptAttempts(current_0 => {
        const next_0 = current_0 + 1;
        persistPermissionPromptAttempts(next_0);
        return next_0;
      });
    }
    setRetriedKind(errorKind);
    setIsRetrying(true);
    try {
      await Promise.resolve(onRetry());
    } finally {
      setIsRetrying(false);
    }
  };

  // Only show the "still blocked" hint for permission-denied, since for the
  // other kinds the body copy already explains what the user needs to do
  // before retrying (plug in camera, close Zoom, reload page, etc.).
  const showRepeatedFailureHint = !isRetrying && errorKind === "permission-denied" && retriedKind === "permission-denied";

  // Surface the in-app browser tip for:
  // - explicit permission-denied immediately,
  // - permission-prompt only after a repeated no-progress retry/escalation,
  // - no-media-devices (common in constrained WebViews that do not expose a
  //   full mediaDevices implementation).
  const isRepeatedPermissionPromptFailure = !isRetrying && errorKind === "permission-prompt" && retriedKind === "permission-prompt";
  const shouldShowEscalatedPromptGuidance = shouldEscalatePermissionPrompt || isRepeatedPermissionPromptFailure;
  // The tip is additive (it never replaces the Try Again CTA) since many
  // in-app browsers do actually grant camera access once the host app's
  // permission is toggled.
  const showInAppBrowserTip = (displayErrorKind === "permission-denied" || shouldShowEscalatedPromptGuidance || errorKind === "no-media-devices") && isInAppBrowser && (platform === "ios" || platform === "android");
  const inAppBrowserTipKey: keyof Dictionary = platform === "ios" ? "assessmentSteps.studioSession.error.camera.permissionDenied.inAppBrowser.ios" : "assessmentSteps.studioSession.error.camera.permissionDenied.inAppBrowser.android";

  // Offer the upload escape hatch only for failure kinds where the live camera
  // is unlikely to come back in this session: explicitly blocked permission,
  // an embed Permissions-Policy block, no camera device, or no usable
  // mediaDevices. `in-use` and `generic` keep a plain retry because the camera
  // typically recovers once the conflicting app closes / the transient error
  // clears. `displayErrorKind` (not `errorKind`) is used so an escalated
  // permission-prompt — which renders as permission-denied — also surfaces it.
  const cameraLikelyUnavailable = displayErrorKind === "permission-denied" || displayErrorKind === "embed-policy-blocked" || displayErrorKind === "no-camera" || displayErrorKind === "no-media-devices";
  const showUploadInstead = Boolean(onUploadInstead) && cameraLikelyUnavailable;
  return <div className="absolute inset-0 z-20 overflow-y-auto overscroll-contain bg-black/70 backdrop-blur-sm [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-error-kind={errorKind} role="alert">
      <div className="flex min-h-full items-center justify-center p-5">
        <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-xl">
          <div className="flex flex-col items-center gap-5 text-center">
            <AlertCircle className="text-destructive h-12 w-12" />

            <div className="space-y-2">
              <h2 className="type-heading-sm text-overlay-foreground">{t(copy.title)}</h2>
              {!copy.instructions && <p className="type-body-sm text-overlay-foreground/85 whitespace-pre-line">
                  {t(copy.body)}
                </p>}
            </div>
          </div>

          {copy.instructions && <ol className="mt-6 space-y-3 border-t border-white/10 pt-5">
              {copy.instructions.map((instructionKey, index) => <li className="grid grid-cols-[1.5rem_1fr] items-baseline gap-x-3" key={instructionKey}>
                  <span aria-hidden="true" className="type-body-md text-overlay-foreground text-right font-medium tabular-nums">
                    {index + 1}
                  </span>
                  <span className="type-body-sm text-overlay-foreground/85">
                    {t(instructionKey)}
                  </span>
                </li>)}
            </ol>}

          {showInAppBrowserTip && <p className="type-caption text-overlay-foreground/80 mt-4 text-center" data-testid="capture-error-overlay-in-app-browser-tip">
              <span className="text-overlay-foreground font-semibold">
                {t("assessmentSteps.studioSession.error.camera.permissionDenied.inAppBrowser.label")}
              </span>
              <span aria-hidden="true" className="mx-1.5">
                —
              </span>
              <span>{t(inAppBrowserTipKey)}</span>
            </p>}

          {showRepeatedFailureHint && <p className="type-caption text-overlay-foreground/75 mt-4 text-center" data-testid="capture-error-overlay-still-blocked-hint">
              {t("assessmentSteps.studioSession.error.stillBlockedHint")}
            </p>}

          <div className="mt-6 flex flex-col gap-3">
            {copy.primaryAction === "retry" ? <Button aria-busy={isRetrying} className="w-full border-white bg-white text-black hover:scale-100 hover:border-white hover:bg-white/90 hover:text-black active:scale-100 disabled:opacity-80" data-testid="capture-error-overlay-retry" disabled={isRetrying} variant="outline" onClick={handleRetryClick}>
                {isRetrying ? <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                <span className="type-body-sm">
                  {t(isRetrying ? "assessmentSteps.studioSession.error.retrying" : "assessmentSteps.studioSession.error.retry")}
                </span>
              </Button> : <Button className="w-full border-white bg-white text-black hover:scale-100 hover:border-white hover:bg-white/90 hover:text-black active:scale-100" data-testid="capture-error-overlay-reload" variant="outline" onClick={onReload}>
                <RefreshCw className="mr-2 h-4 w-4" />
                <span className="type-body-sm">
                  {t("assessmentSteps.studioSession.error.reload")}
                </span>
              </Button>}

            {showUploadInstead && <Button className="w-full border-white/30 bg-transparent text-white hover:scale-100 hover:border-white hover:bg-white/10 hover:text-white active:scale-100 disabled:opacity-50" data-testid="capture-error-overlay-upload-instead" disabled={isRetrying} type="button" variant="outline" onClick={onUploadInstead}>
                <span className="type-body-sm">
                  {t("assessmentSteps.studioSession.tips.uploadInstead")}
                </span>
              </Button>}

            {copy.secondaryAction === "reload" && <button className="type-caption text-overlay-foreground/75 hover:text-overlay-foreground flex min-h-11 w-full items-center justify-center rounded-full underline-offset-4 transition-colors hover:underline disabled:opacity-50" data-testid="capture-error-overlay-reload-link" disabled={isRetrying} type="button" onClick={onReload}>
                {t("assessmentSteps.studioSession.error.reload")}
              </button>}
          </div>
        </div>
      </div>
    </div>;
}